import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { roundOffAndFormatNumberWithCurrency } from '../../../../utils/utils'
import { postCTA } from '../../../../utils/appBridge'
import {
  TaxHarvestingScreenName,
  TaxHarvestingBottomSheetName,
  TaxHarvestingEventId,
  TaxHarvestingButtonLabel,
  trackTaxHarvestingBottomSheetLoaded,
  trackTaxHarvestingUserClicked,
  trackTaxHarvestingButtonTap
} from '../../tracking/taxHarvestingTracking'
import { LockIcon } from '../LockIcon'
import RenderHTML from '../../../../components/ui-components/RenderHTML'
import { EligibleFund } from './EligibleFund'
import type { HarvestingPlanFund } from './PlanFund'
import type { HarvestingPlanType } from '../../types/summary.types'
import type { TaxHarvestingPlanFundDetail } from '../../api/planDetailApi'
import BottomSheetContent from '../../../../components/BottomSheetComponents/BottomSheetContent'
import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import { SecondaryButton } from '../../../../components/execution/SecondaryButton'
import { TaxSVG } from '../TaxSVG'
import { PrimaryButton } from '../../../../components/execution/PrimaryButton'
import { getEliteCTA } from '../../../../utils/mutualFundDetail'
import { planMockFunds } from '../../mock/data/planMockFunds'
import { One } from '../../../../icons/rebalance/One'
import { Two } from '../../../../icons/rebalance/Two'
import { Three } from '../../../../icons/rebalance/Three'

type TaxSavingsPlanProps = {
  /** When true, blur the plan and show Elite unlock overlay */
  showEliteLockOverlay?: boolean
  /** Harvest type from query params (e.g. EQ_STCL / EQ_STCG) */
  harvestType?: string | null
  /** Total funds count from plan detail response (used for eligible funds label) */
  totalFundCount?: number
  /** Backend-driven plan details; when user is elite and data is present, this replaces fallback funds */
  eligiblePlanDetails?: TaxHarvestingPlanFundDetail[]
  /** Additional funds only shown when user taps "View all eligible funds" */
  remainingPlanDetails?: TaxHarvestingPlanFundDetail[]
  /** From summary API: total possible tax save for this plan */
  summaryPossibleTaxSave?: number
  /** From summary API: total loss to book for this plan */
  summaryGainBookAmount?: number
  /** Possible harvesting types for the current plan (from summary); sent to recommendation as comma-separated */
  possibleFolioTaxHarvestingTypes?: HarvestingPlanType[]
  /** When true, render inside bottom sheet: all funds, no "View all" button; requires onDismiss */
  isInBottomSheet?: boolean
  /** Required when isInBottomSheet; called when sheet is dismissed */
  onDismiss?: () => void
}

function TaxSavingsPlanContent({
  showEliteLockOverlay,
  fundsForDisplay,
  eligibleFundsCount,
  harvestType,
  possibleFolioTaxHarvestingTypes,
  isInBottomSheet,
  onViewAllClick,
  onHowLossClick,
  formatCurrency,
  taxSaveTotal,
  totalLossToBook,
  hasRemainingFunds,
  onBottomSheetDismiss
}: {
  showEliteLockOverlay: boolean
  fundsForDisplay: HarvestingPlanFund[]
  eligibleFundsCount: number
  harvestType: string | null | undefined
  possibleFolioTaxHarvestingTypes?: HarvestingPlanType[]
  isInBottomSheet: boolean
  onViewAllClick?: () => void
  onHowLossClick: () => void
  formatCurrency: (value: number) => string
  taxSaveTotal: number
  totalLossToBook: number
  hasRemainingFunds: boolean
  onBottomSheetDismiss?: () => void
}) {
  const navigate = useNavigate()
  const showViewAllButton = !showEliteLockOverlay && !isInBottomSheet && hasRemainingFunds

  return (
    <>
      <div className='px-5 flex flex-row items-center gap-1 font-medium'>
        <span>
          <TaxSVG fill='#992E00' />
        </span>
        <span className='text-sm text-orange10'>Tax Saving Plan</span>
      </div>

      <div className='px-5 mt-3 text-[22px] leading-[1.3]'>
        <span className='font-medium text-[#000000]'>Save {formatCurrency(taxSaveTotal)} in tax</span>{' '}
        <span className='text-grey40'>
          by booking {formatCurrency(totalLossToBook)} losses from any of these
          funds
        </span>
      </div>

      <div
        onClick={() => {
          trackTaxHarvestingUserClicked({
            screen: TaxHarvestingScreenName.Plan,
            eventId: TaxHarvestingEventId.HowLossBookingSavesTax
          })
          onHowLossClick()
        }}
        className='mt-3 text-[13px] text-grey40 underline cursor-pointer px-5'
      >
        How can booking losses save tax?
      </div>

      <div className={showEliteLockOverlay ? 'blur-lg pointer-events-none select-none' : ''}>
        <div
          className={`p-5 mt-6 border-t border-b border-grey80 flex items-center justify-between text-[13px] text-[#3D3D3D] 
            ${isInBottomSheet ? 'sticky top-0 z-10 bg-white' : ''}`}
        >
          <span>{eligibleFundsCount} eligible funds</span>
          <span></span>
        </div>

        <div className='mt-4 flex flex-col gap-0'>
          {fundsForDisplay.map((fund, idx) => (
            <EligibleFund
              key={fund.isin}
              fundLogo={fund.fundLogo}
              fundTitle={fund.fundName}
              fundNature={fund.fundNature}
              fundCategory={fund.fundCategory}
              amount={fund.bookGain}
              powerRank={fund.rank}
              powerRating={fund.fundPerformance ?? undefined}
              isRegular={false}
              saveTaxAmount={fund.saveTax}
              isFutureTax={false}
              isGains={false}
              isLast={idx === fundsForDisplay.length - 1}
              onClick={() => {
                trackTaxHarvestingUserClicked({
                  screen: TaxHarvestingScreenName.Plan,
                  eventId: TaxHarvestingEventId.TaxSavingsFundCard,
                  fundIsin: fund.isin
                })
                onBottomSheetDismiss?.()
                const base = `/tax-harvesting/recommendation?isin=${fund.isin}&harvestType=${harvestType ?? ''}&amount=${fund.bookGain}&saveTax=${fund.saveTax}`
                const possibleParam =
                  Array.isArray(possibleFolioTaxHarvestingTypes) && possibleFolioTaxHarvestingTypes.length > 0
                    ? `&possibleFolioTaxHarvestingTypes=${possibleFolioTaxHarvestingTypes.join(',')}`
                    : ''
                navigate(`${base}${possibleParam}`)
              }}
            />
          ))}
        </div>
        {showViewAllButton && onViewAllClick && (
          <div className='w-full px-5 mt-6 mb-2'>
            <SecondaryButton
              onClick={() => {
                trackTaxHarvestingUserClicked({
                  screen: TaxHarvestingScreenName.Plan,
                  eventId: TaxHarvestingEventId.ViewAllTaxSavingsFunds
                })
                onViewAllClick?.()
              }}
              label='View all eligible funds'
            />
          </div>
        )}
      </div>
    </>
  )
}

export default function TaxSavingsPlan({
  showEliteLockOverlay = false,
  harvestType,
  totalFundCount,
  eligiblePlanDetails,
  remainingPlanDetails,
  summaryPossibleTaxSave,
  summaryGainBookAmount,
  possibleFolioTaxHarvestingTypes,
  isInBottomSheet = false,
  onDismiss
}: TaxSavingsPlanProps) {
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()

  const eligibleFunds: HarvestingPlanFund[] = Array.isArray(eligiblePlanDetails) ? eligiblePlanDetails : []
  const remainingFunds: HarvestingPlanFund[] = Array.isArray(remainingPlanDetails) ? remainingPlanDetails : []
  const hasRemainingFunds = remainingFunds.length > 0

  const allFunds: HarvestingPlanFund[] = [...eligibleFunds, ...remainingFunds]
  const baseFundsForPage: HarvestingPlanFund[] = eligibleFunds.length > 0 ? eligibleFunds : allFunds

  const effectiveFundsForDisplay: HarvestingPlanFund[] = showEliteLockOverlay
    ? planMockFunds.map((f) => ({
      ...f,
      bookGain: -f.bookGain,
      saveTax: Math.round(f.bookGain * 0.125)
    }))
    : isInBottomSheet
      ? allFunds
      : baseFundsForPage

  if (effectiveFundsForDisplay.length === 0) {
    return null
  }

  const taxSaveTotal =
    summaryPossibleTaxSave ??
    eligibleFunds.reduce((sum, f) => sum + f.saveTax, 0)

  const totalLossToBook =
    summaryGainBookAmount ??
    eligibleFunds.reduce((sum, f) => sum + Math.abs(f.bookGain), 0)
  const formatCurrency = (value: number) => `${roundOffAndFormatNumberWithCurrency(value)}`

  const handleViewAllClick = () => {
    openBottomSheet(
      <TaxSavingsPlan
        showEliteLockOverlay={false}
        totalFundCount={totalFundCount}
        eligiblePlanDetails={eligiblePlanDetails}
        remainingPlanDetails={remainingPlanDetails}
        summaryPossibleTaxSave={summaryPossibleTaxSave}
        summaryGainBookAmount={summaryGainBookAmount}
        harvestType={harvestType}
        possibleFolioTaxHarvestingTypes={possibleFolioTaxHarvestingTypes}
        isInBottomSheet
        onDismiss={closeBottomSheet}
      />,
      closeBottomSheet
    )
  }

  const handleHowLossClick = () => {
    openBottomSheet(
      <HowLossBookingReducesTaxSheet onDismiss={closeBottomSheet} />,
      closeBottomSheet
    )
  }

  const content = (
    <TaxSavingsPlanContent
      showEliteLockOverlay={showEliteLockOverlay}
      fundsForDisplay={effectiveFundsForDisplay}
      eligibleFundsCount={totalFundCount ?? eligibleFunds.length}
      harvestType={harvestType}
      possibleFolioTaxHarvestingTypes={possibleFolioTaxHarvestingTypes}
      isInBottomSheet={isInBottomSheet}
      onViewAllClick={handleViewAllClick}
      onHowLossClick={handleHowLossClick}
      formatCurrency={formatCurrency}
      taxSaveTotal={taxSaveTotal}
      totalLossToBook={totalLossToBook}
      hasRemainingFunds={hasRemainingFunds}
      onBottomSheetDismiss={closeBottomSheet}
    />
  )

  if (isInBottomSheet && onDismiss) {
    return (
      <BottomSheetContent heading='' bottomSheetHeadClassName='!pt-2' bottomSheetBodyClassName='!px-0 !overflow-visible' onDismiss={onDismiss}>
        <div className='py-4'>{content}</div>
      </BottomSheetContent>
    )
  }

  return (
    <div className='py-8 relative'>
      <div>{content}</div>
      {showEliteLockOverlay ? <TaxSavingsPlanLockOverlay /> : null}
    </div>
  )
}

type HowLossBookingReducesTaxSheetProps = {
  onDismiss: () => void
}

export function HowLossBookingReducesTaxSheet({ onDismiss }: HowLossBookingReducesTaxSheetProps) {
  useEffect(() => {
    trackTaxHarvestingBottomSheetLoaded(TaxHarvestingBottomSheetName.HowLossBookingSavesTax)
  }, [])
  return (
    <BottomSheetContent heading="How does booking losses save tax?" onDismiss={onDismiss}>
      <div className='pt-2 pb-4 flex flex-col gap-5 text-[14px] text-[#232323]'>
        <div className='flex items-start gap-3'>
          <div className='mt-[2px] flex h-6 w-6 flex-shrink-0
          items-center justify-center rounded-full border border-[#D0D5DD] text-[12px] text-[#3D3D3D]'>
            <One />
          </div>
          <p className='leading-[1.5] text-[#3D3D3D]'>
            Tax is paid only on the gains you make.
          </p>
        </div>
        <div className='flex items-start gap-3'>
          <div className='mt-[2px] flex h-6 w-6 flex-shrink-0
          items-center justify-center rounded-full border border-[#D0D5DD] text-[12px] text-[#3D3D3D]'>
            <Two />
          </div>
          <p className='leading-[1.5] text-[#3D3D3D]'>
            And your taxable gains are reduced by any losses you book. (Gains - loss = net gains)
          </p>
        </div>
        <div className='flex items-start gap-3'>
          <div className='mt-[2px] flex h-6 w-6 flex-shrink-0
          items-center justify-center rounded-full border border-[#D0D5DD] text-[12px] text-[#3D3D3D]'>
            <Three />
          </div>
          <p className='leading-[1.5] text-[#3D3D3D]'>
            Result: you pay less tax on net gains.
          </p>
        </div>
        <p className='leading-[1.5] text-[#3D3D3D] pl-9'>
          When you reinvest immediately, your investment keeps growing while your tax liability reduces.
        </p>
      </div>
    </BottomSheetContent>
  )
}

export const TaxSavingsPlanLockOverlay = () => {
  return (
    <div className='absolute inset-0 z-10 flex items-center justify-center px-8 pointer-events-none'>
      <div className='flex flex-col items-center gap-4 text-center max-w-[286px] pointer-events-auto'>
        <RenderHTML innerHTMLText={LockIcon} />
        <p className='text-[16px] font-[400] leading-[150%] text-[#232323]'>
          See which funds to sell and exactly how much to book these gains
        </p>
        <p className='text-[13px] text-[#CC5C2D] leading-[150%] font-[400]'>
          Avail tax benefits before it expires on 30 March
        </p>
        <div className='pt-1'>
          <PrimaryButton
            onClick={() => {
              trackTaxHarvestingButtonTap({
                screen: TaxHarvestingScreenName.Plan,
                eventId: TaxHarvestingEventId.UnlockEliteOverlay,
                buttonLabel: TaxHarvestingButtonLabel.UnlockPlanWithElite
              })
              postCTA(JSON.stringify(getEliteCTA()))
            }}
            label='Unlock plan with Elite'
            primaryButtonClassName='text-[14px]'
          />
        </div>
      </div>
    </div>
  )
}

